import { motion } from "framer-motion";
import { Award, GraduationCap, Sparkles, TrendingUp, Cpu, Briefcase, ShieldCheck, BookOpen } from "lucide-react";
import { openBooking, openPartners } from "../lib/site";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton } from "../components/site/Primitives";
import Testimonials from "../components/site/Testimonials";

const GIO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_beauty-courses-pro/artifacts/pmt8kuxq_NAIL%20.NEW%20WEBSITE.png";

const CREDENTIALS = [
  { icon: Award, title: "20+ Years of Industry Experience", copy: "Two decades of practice — behind the nail table and in the classroom." },
  { icon: GraduationCap, title: "NYS Approved Instructor", copy: "State-approved to prepare candidates for the NY nail specialty licensing exam." },
  { icon: Briefcase, title: "Certified Workforce Trainer", copy: "Certified to design and deliver workforce and vocational training." },
  { icon: Cpu, title: "AI Business Consultant", copy: "Practical AI, automation, and marketing systems for small businesses." },
  { icon: Sparkles, title: "Certified Master Pedicurist", copy: "Elevated, health-aware luxury foot care and salon standards." },
  { icon: TrendingUp, title: "Salon Growth Strategist", copy: "1:1 coaching on branding, pricing, systems, and scale." },
  { icon: ShieldCheck, title: "Government Contract Ready", copy: "Prepared to partner on RFPs, grants, and workforce contracts." },
];

const PARAGRAPHS = [
  "More than twenty years ago, I began my career in beauty with one simple goal \u2014 to help people feel confident. What started behind the nail table quickly became something much bigger.",
  "As I mentored aspiring professionals, I realized many weren\u2019t just looking to learn a trade \u2014 they were looking for a pathway to a better life. They needed guidance, business skills, and the confidence to build careers that lasted.",
  "That\u2019s why I created Nervani Beauty Boutique LLC \u2014 where education, workforce development, and entrepreneurship come together.",
  "Today, I help individuals earn professional licensure, entrepreneurs build stronger businesses, and organizations use technology and AI to grow. My expertise was built in beauty \u2014 as a New York State Approved Instructor, Certified Master Pedicurist, and salon growth strategist \u2014 but my mission has expanded far beyond it.",
  "Every profession deserves access to real education, modern tools, and room to grow. Whether you\u2019re launching a career, scaling a business, or looking for a partner in workforce development \u2014 I\u2019m here to help you build lasting success.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About Me"
        title="Meet Giouvanna White."
        subtitle="I believe education creates opportunity."
      />

      <section className="bg-brand-cream py-24 md:py-32" data-testid="about-story">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-start">
          <Reveal>
            <div className="relative flex items-end justify-center h-[460px] lg:h-[720px]">
              <div className="absolute inset-x-4 sm:inset-x-10 top-4 bottom-0 rounded-t-full rounded-b-[2.5rem] bg-gradient-to-b from-brand-pink to-brand-blush" />
              <div className="absolute inset-x-4 sm:inset-x-10 top-4 bottom-0 rounded-t-full rounded-b-[2.5rem] border border-brand-gold/40" />
              <img src={GIO_IMG} alt="Giouvanna White, founder of Nervani Beauty Boutique" className="relative z-10 h-full w-auto object-contain object-bottom mix-blend-multiply" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline className="mb-5">The founder</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              From behind the nail table to building careers across industries.
            </h2>
            <div className="space-y-5 text-brand-ink/75 font-light mt-7 leading-relaxed text-base md:text-lg">
              {PARAGRAPHS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p className="font-serif italic text-brand-gold text-xl md:text-2xl pt-2">
                Because when people gain skills, they gain opportunity — and that changes lives.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-9">
              <PrimaryButton as="link" to="/programs" data-testid="about-programs-btn">Explore Programs</PrimaryButton>
              <GoldButton onClick={openPartners} data-testid="about-partners-btn">Partner With Us</GoldButton>
              <button
                onClick={openBooking}
                data-testid="about-book-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full text-sm tracking-wide font-medium border border-brand-ink/25 text-brand-ink px-8 py-3.5 hover:border-brand-ink hover:bg-brand-ink hover:text-brand-cream transition-colors"
              >
                Book a Service
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-blush py-24 md:py-32" data-testid="about-credentials">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="text-center flex flex-col items-center mb-14">
            <Overline className="mb-5">Credentials</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight max-w-2xl">A foundation of expertise you can trust.</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CREDENTIALS.map((c, i) => (
              <motion.div key={c.title} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 }} className="bg-white rounded-3xl border border-brand-ink/10 p-7">
                <span className="h-12 w-12 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose mb-5"><c.icon size={22} strokeWidth={1.6} /></span>
                <h3 className="font-serif text-xl text-brand-ink tracking-tight leading-tight">{c.title}</h3>
                <p className="text-brand-ink/70 font-light mt-3 text-sm leading-relaxed">{c.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials heading="Loved by students & clients" />
    </div>
  );
}
